import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useCallback } from "react";
import { useDeleteScheduleApplicationMutation } from "@api/scheduleTemplate.api";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { ScheduleDeleteJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScheduleChange } from "@components/accounting/schedules/change/useScheduleChange";
import { useApplyScheduleWithLinkedConflict } from "@components/accounting/schedules/linkedConflict/useApplyScheduleWithLinkedConflict";
import {
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "@components/accounting/schedules/manual/scheduleSpecificDateApply";
import { useScheduleSpecificDateApply } from "@components/accounting/schedules/manual/useScheduleSpecificDateApply";
import {
    opensScheduleModalBeforeApply,
    SchedulePickerTemplateWithAnchors,
} from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { buildInvoiceSchedulePayload } from "@components/invoices/useApplyInvoiceSchedule";
import { buildTransactionSchedulePayload } from "@components/transactions/views/transactionSchedulePayload";
import { MatchItemType, MatchableItem } from "../types";
import {
    buildMatchItemSpecificDateRequest,
    parseMatchableItemSource,
} from "./matchItemSchedule";

function buildPayload(options: {
    item: MatchableItem;
    specificDateApply?: ScheduleSpecificDateApplyResult;
}): ApplyScheduleTemplateBody | null {
    const { item, specificDateApply } = options;
    const source = parseMatchableItemSource(item);
    if (!source) {
        return null;
    }
    if ("transactionId" in source) {
        return buildTransactionSchedulePayload({
            entityId: item.entityId,
            transactionId: source.transactionId,
            amount: item.amount,
            specificDateApply,
        });
    }
    return buildInvoiceSchedulePayload({
        entityId: item.entityId,
        invoiceId: source.invoiceId,
        amount: item.amount,
        ...specificDateApply,
    });
}

/**
 * Apply / replace / remove for one match row. Every write commits at once
 * through the same lifecycle calls the grids use; nothing waits for Confirm.
 */
export function useMatchItemScheduleChange(options: {
    item: MatchableItem;
    application: ScheduleApplicationListItem | null;
    showLockedPeriods: ShowLockedPeriods;
}) {
    const { item, application, showLockedPeriods } = options;
    const { toast, error } = useToaster();
    const { applyWithLinkedConflict, linkedConflictModal, isApplying } =
        useApplyScheduleWithLinkedConflict({ showLockedPeriods });
    const deleteMutation = useDeleteScheduleApplicationMutation();

    const runApply = useCallback(
        async (applyOptions: {
            templateId: string;
            specificDateApply?: ScheduleSpecificDateApplyResult;
        }): Promise<boolean> => {
            const payload = buildPayload({
                item,
                specificDateApply: applyOptions.specificDateApply,
            });
            if (!payload) {
                return false;
            }
            try {
                const result = await applyWithLinkedConflict({
                    templateId: applyOptions.templateId,
                    payload,
                });
                if (result.outcome === "applied") {
                    toast("Schedule applied");
                }
                // The conflict modal finishes the apply; not a failure.
                return true;
            } catch (applyError) {
                if (!showLockedPeriods(applyError)) {
                    error(
                        applyError instanceof Error
                            ? applyError.message
                            : "Failed to apply schedule",
                    );
                }
                return false;
            }
        },
        [applyWithLinkedConflict, error, item, showLockedPeriods, toast],
    );

    const applyPending = useCallback(
        (
            pending: ScheduleSpecificDateApplyRequest,
            result: ScheduleSpecificDateApplyResult,
        ) =>
            runApply({
                templateId: pending.templateId,
                specificDateApply: result,
            }),
        [runApply],
    );
    const { open: openSpecificDate, modal: specificDateModal } =
        useScheduleSpecificDateApply({ apply: applyPending, isApplying });

    const applySchedule = useCallback(
        async (
            template: SchedulePickerTemplateWithAnchors,
        ): Promise<boolean> => {
            if (opensScheduleModalBeforeApply(template)) {
                const request = buildMatchItemSpecificDateRequest({
                    item,
                    template,
                });
                return request ? await openSpecificDate(request) : false;
            }
            return await runApply({ templateId: template.id });
        },
        [item, openSpecificDate, runApply],
    );

    const removeCurrent = useCallback(
        async (journalEntryMode: ScheduleDeleteJournalEntryMode) => {
            if (!application) {
                return false;
            }
            try {
                await deleteMutation.mutateAsync({
                    applicationId: application.id,
                    entityId: application.entityId,
                    journalEntryMode,
                });
                toast("Schedule removed");
                return true;
            } catch (removeError) {
                if (!showLockedPeriods(removeError)) {
                    error(
                        removeError instanceof Error
                            ? removeError.message
                            : "Failed to remove schedule",
                    );
                }
                return false;
            }
        },
        [application, deleteMutation, error, showLockedPeriods, toast],
    );

    const { onSelect, onClear, confirmModal } = useScheduleChange({
        source: item.type === MatchItemType.INVOICE ? "invoice" : "transaction",
        current: application
            ? {
                  scheduleApplicationId: application.id,
                  scheduleTemplateId: application.scheduleTemplateId,
                  scheduleName: application.scheduleTemplateName,
              }
            : null,
        applySchedule,
        removeCurrent,
    });

    return {
        onSelect,
        onClear,
        confirmModal,
        linkedConflictModal,
        specificDateModal,
        isBusy: isApplying || deleteMutation.isPending,
    };
}
